import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AccountMapper } from '@core/mapper/accountMapper';
import { Account } from '@core/model/domain/account';
import { GetAccountsResponse } from '@core/service/digital-factory/account/types/getAccounts';
import { PostRequestAnalysisResponse } from '@core/service/digital-factory/account/types/postRequestAnalysis';
import {
  SelectAccountsResponse,
  SynchronizeAccountsRequest,
} from '@core/service/digital-factory/account/types/putSelectAccounts';
import { SelectAccountsRequest } from '@core/service/digital-factory/account/types/selectAccountsDTOs';
import {
  WaitOnCaseStateNotification,
  WaitOnCaseStateNotificationOutput,
} from '@core/usecase/waitOnCaseStateNotification';
import { environment } from '@environments/environment';
import { Observable, catchError, delay, map, of, switchMap, tap, throwError, timer } from 'rxjs';
import urlJoin from 'url-join';

@Injectable({
  providedIn: 'root',
})
export class AccountsService {
  private urlOpenBanking = environment.urlOpenBanking;
  private requestAnalysisPollingConfiguration = environment.pollingConfigurations.requestAnalysis;
  private analysisWorkflowDelayBetweenCallsInMs =
    environment.pollingConfigurations.analysisWorkflowDelayBetweenCallsInMs;

  private accountMapper = new AccountMapper();

  private delayBeforeGetAccount = 1000;

  constructor(
    private http: HttpClient,
    private waitOnCaseStateNotification: WaitOnCaseStateNotification
  ) {}

  selectAccounts(input: {
    userToken: string;
    userId: string;
    connectionId: string;
    request: SelectAccountsRequest;
  }): Observable<SelectAccountsResponse> {
    const { userId, userToken, request, connectionId } = input;

    if (request.accounts.length === 0) {
      throw new Error('No accounts selected.');
    }

    const synchronizeAccountsUrl = urlJoin(
      this.urlOpenBanking,
      'users',
      userId,
      'connections',
      connectionId,
      'accounts'
    );

    const synchronizeAccountsRequestBody: SynchronizeAccountsRequest = {
      accounts: request.accounts.map((account) => {
        return {
          id: account.id,
          isSynchronizationEnabled: true,
        };
      }),
    };

    return this.http.put<SelectAccountsResponse>(synchronizeAccountsUrl, synchronizeAccountsRequestBody, {
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    });
  }

  requestAnalysis(input: {
    userToken: string;
    userId: string;
    bankAccountsIds: string[];
  }): Observable<PostRequestAnalysisResponse> {
    const { userToken, userId, bankAccountsIds } = input;

    const requestAnalysisUrl = urlJoin(
      this.urlOpenBanking,
      'users',
      userId,
      'open-banking-case-clerk',
      'request-analysis'
    );

    return this.http
      .post<PostRequestAnalysisResponse>(
        requestAnalysisUrl,
        {
          bankAccountsIds,
        },
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        }
      )
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((err, _) => {
          if (err.error) {
            return of(err.error) as Observable<PostRequestAnalysisResponse>;
          }

          throw err;
        })
      );
  }

  requestAnalysisWithPolling(input: {
    userToken: string;
    userId: string;
    bankAccountsIds: string[];
    retryCount?: number;
  }): Observable<PostRequestAnalysisResponse> {
    const { userId, userToken, bankAccountsIds } = input;
    const retryCount = input.retryCount ?? 0;

    const { maxRetries, retryDelayInMs } = this.requestAnalysisPollingConfiguration;

    return this.requestAnalysis({ userId, userToken, bankAccountsIds }).pipe(
      switchMap((res) => {
        if ('error' in res) {
          const notSynchronizedYet = res.error.reason == 'SOME_BANK_ACCOUNTS_ARE_NOT_SYNCHRONIZED_YET';
          if (!notSynchronizedYet) {
            return throwError(() => res);
          }
          if (retryCount < maxRetries) {
            return timer(retryDelayInMs).pipe(
              switchMap(() =>
                this.requestAnalysisWithPolling({
                  userId,
                  userToken,
                  bankAccountsIds,
                  retryCount: retryCount + 1,
                })
              )
            );
          } else {
            return throwError(() => 'Max retries reached');
          }
        } else {
          return of(res);
        }
      })
    );
  }

  selectAccountsThenRequestAnalysisThenWaitForCaseState(input: {
    userId: string;
    userToken: string;
    connectionId: string;
    selectAccountsRequest: SelectAccountsRequest;
  }): Observable<Awaited<WaitOnCaseStateNotificationOutput>> {
    const { userId, userToken, selectAccountsRequest, connectionId } = input;
    const selectedAccountsIds = selectAccountsRequest.accounts.map((account) => account.id);

    return this.selectAccounts({
      userId,
      userToken,
      connectionId,
      request: selectAccountsRequest,
    }).pipe(
      tap(() => console.log('Delaying after selecting accounts to wait for synchronization')),
      delay(this.analysisWorkflowDelayBetweenCallsInMs),
      switchMap(() =>
        this.requestAnalysisWithPolling({
          userId,
          userToken,
          bankAccountsIds: selectedAccountsIds,
        }).pipe(
          tap(() => console.log('Delaying after request analysis to wait for processing')),
          delay(this.analysisWorkflowDelayBetweenCallsInMs),
          switchMap(
            async () =>
              await this.waitOnCaseStateNotification.execute({
                userId,
                userToken,
              })
          ),
          switchMap((res) => {
            return of(res);
          })
        )
      )
    );
  }

  getAccounts(input: { userId: string; connectionId: string; userToken: string }): Observable<Account[]> {
    const { userId, connectionId, userToken } = input;

    const getAccountsUrl = urlJoin(
      this.urlOpenBanking,
      'users',
      userId,
      'connections',
      connectionId,
      'accounts'
    );

    return new Observable((observer) =>
      timer(this.delayBeforeGetAccount)
        .pipe(
          switchMap(() =>
            this.http.get<GetAccountsResponse>(getAccountsUrl, {
              headers: {
                Authorization: `Bearer ${userToken}`,
              },
            })
          )
        )
        .subscribe({
          next: (response) => {
            const mappedAccounts = response.map((account) => this.accountMapper.toDomain(account));

            observer.next(mappedAccounts);
          },
          error: (error) => {
            observer.error(error);
          },
        })
    );
  }
}
